/**
 * Small form building blocks shared by the listing admin tabs. Every input is at least 44px tall on phones.
 */
import React, { useCallback, useEffect, useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import InfoTooltip from '../../common/InfoTooltip';

export const INPUT_CLASS =
  'w-full min-h-[44px] px-3 py-2 rounded-[10px] border border-ink bg-paper text-ink placeholder:text-ash focus:outline-none focus:ring-2 focus:ring-ink/30 focus:border-ink text-sm';

const LABEL_CLASS = 'flex items-center gap-1 text-sm font-medium text-ink mb-1';

interface LabelProps {
  label: string;
  info?: string;
  hint?: string;
}

function FieldLabel({ htmlFor, label, info }: LabelProps & { htmlFor: string }) {
  return (
    <label htmlFor={htmlFor} className={LABEL_CLASS}>
      {label}
      {info && <InfoTooltip text={info} />}
    </label>
  );
}

function Hint({ hint }: { hint?: string }) {
  return hint ? <p className="mt-1 text-xs text-ash">{hint}</p> : null;
}

interface TextFieldProps extends LabelProps {
  value: string;
  onChange: (value: string) => void;
  /** 'rtl' for Hebrew fields, 'ltr' for English ones. */
  dir?: 'rtl' | 'ltr';
  type?: 'text' | 'tel' | 'email' | 'url' | 'number';
  maxLength?: number;
  placeholder?: string;
  required?: boolean;
  autoComplete?: string;
  inputMode?: React.HTMLAttributes<HTMLInputElement>['inputMode'];
}

export function TextField({ label, info, hint, value, onChange, dir, type = 'text', ...rest }: TextFieldProps) {
  const id = useId();
  return (
    <div>
      <FieldLabel htmlFor={id} label={label} info={info} />
      <input
        id={id}
        type={type}
        dir={dir}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className={INPUT_CLASS}
        {...rest}
      />
      <Hint hint={hint} />
    </div>
  );
}

export function TextArea({
  label,
  info,
  hint,
  value,
  onChange,
  dir,
  rows = 3,
  maxLength,
  placeholder,
  mixedLanguages,
}: Omit<TextFieldProps, 'type'> & {
  rows?: number;
  /** Hebrew and English lines together: each line gets its own direction. */
  mixedLanguages?: boolean;
}) {
  const id = useId();
  return (
    <div>
      <FieldLabel htmlFor={id} label={label} info={info} />
      <textarea
        id={id}
        dir={dir}
        rows={rows}
        value={value}
        maxLength={maxLength}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        className={INPUT_CLASS}
        style={mixedLanguages ? { unicodeBidi: 'plaintext' } : undefined}
      />
      <Hint hint={hint} />
    </div>
  );
}

/** Hebrew + English pair, Hebrew first. */
export function BilingualField({
  labelHe,
  labelEn,
  valueHe,
  valueEn,
  onChangeHe,
  onChangeEn,
  multiline,
  maxLength,
  info,
  placeholderHe,
  placeholderEn,
}: {
  labelHe: string;
  labelEn: string;
  valueHe: string;
  valueEn: string;
  onChangeHe: (v: string) => void;
  onChangeEn: (v: string) => void;
  multiline?: boolean;
  maxLength?: number;
  info?: string;
  placeholderHe?: string;
  placeholderEn?: string;
}) {
  const Field = multiline ? TextArea : TextField;
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
      <Field label={labelHe} info={info} value={valueHe} onChange={onChangeHe} dir="rtl" maxLength={maxLength} placeholder={placeholderHe} />
      <Field label={labelEn} value={valueEn} onChange={onChangeEn} dir="ltr" maxLength={maxLength} placeholder={placeholderEn} />
    </div>
  );
}

export function SelectField<T extends string>({
  label,
  info,
  hint,
  value,
  onChange,
  options,
}: LabelProps & { value: T | ''; onChange: (v: T) => void; options: { value: T | ''; label: string }[] }) {
  const id = useId();
  return (
    <div>
      <FieldLabel htmlFor={id} label={label} info={info} />
      <select id={id} value={value} onChange={(e) => onChange(e.target.value as T)} className={INPUT_CLASS}>
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
      <Hint hint={hint} />
    </div>
  );
}

export function Checkbox({
  label,
  checked,
  onChange,
  hint,
}: {
  label: string;
  checked: boolean;
  onChange: (v: boolean) => void;
  hint?: string;
}) {
  const hintId = useId();
  return (
    <div>
      <label className="flex items-center gap-3 min-h-[44px] text-sm text-ink cursor-pointer">
        <input
          type="checkbox"
          checked={checked}
          onChange={(e) => onChange(e.target.checked)}
          aria-describedby={hint ? hintId : undefined}
          className="w-5 h-5 shrink-0 rounded border-ink text-ink focus:ring-ink"
        />
        {label}
      </label>
      {hint && (
        <p id={hintId} className="mt-1 text-xs text-ash">
          {hint}
        </p>
      )}
    </div>
  );
}

/** Tap-to-toggle chips, for picking several options (days, languages, topics). */
export function ChipSelect<T extends string | number>({
  label,
  info,
  options,
  selected,
  onChange,
}: {
  label: string;
  info?: string;
  options: { value: T; label: string }[];
  selected: T[];
  onChange: (next: T[]) => void;
}) {
  const toggle = (value: T) =>
    onChange(
      selected.includes(value)
        ? selected.filter((v) => v !== value)
        : options.map((o) => o.value).filter((v) => v === value || selected.includes(v))
    );
  return (
    <fieldset>
      <legend className={LABEL_CLASS}>
        {label}
        {info && <InfoTooltip text={info} />}
      </legend>
      <div className="flex flex-wrap gap-2">
        {options.map((o) => {
          const on = selected.includes(o.value);
          return (
            <button
              key={String(o.value)}
              type="button"
              aria-pressed={on}
              onClick={() => toggle(o.value)}
              className={`min-h-[44px] px-3 rounded-full border text-sm transition-colors ${
                on ? 'bg-ink text-paper border-ink' : 'bg-paper text-ink border-ink/20 hover:border-ink/50'
              }`}
            >
              {o.label}
            </button>
          );
        })}
      </div>
    </fieldset>
  );
}

export function SectionTitle({ children, level = 3 }: { children: React.ReactNode; level?: 2 | 3 }) {
  // Headings must not skip a level (IS 5568): pages without an h2 above use level 2.
  const Tag = level === 2 ? 'h2' : 'h3';
  return <Tag className="text-base font-semibold text-ink">{children}</Tag>;
}

/** List of problems (translated keys under directory.admin.error). */
export function ErrorList({ errors }: { errors: string[] }) {
  const { t } = useTranslation();
  if (errors.length === 0) return null;
  return (
    <ul role="alert" className="rounded-[10px] border border-danger-500 bg-paper px-3 py-2 text-sm text-danger-600 space-y-1">
      {errors.map((e) => (
        <li key={e}>{t(`directory.admin.error.${e}`)}</li>
      ))}
    </ul>
  );
}

/** Save / Cancel row. Shows "Saved" briefly after a save. */
export function SaveBar({
  dirty,
  saving,
  onSave,
  onCancel,
  justSaved,
  failed,
}: {
  dirty: boolean;
  saving: boolean;
  onSave: () => void;
  onCancel: () => void;
  justSaved: boolean;
  failed: boolean;
}) {
  const { t } = useTranslation();
  return (
    <div className="sticky bottom-0 -mx-4 px-4 py-3 bg-paper/95 backdrop-blur border-t border-ink/10 flex flex-wrap items-center gap-3">
      <button
        type="button"
        onClick={onSave}
        disabled={!dirty || saving}
        className="min-h-[44px] px-5 rounded-[10px] bg-ink text-paper text-sm font-medium hover:opacity-90 transition-opacity disabled:opacity-40"
      >
        {saving ? t('directory.admin.saving') : t('directory.admin.save')}
      </button>
      {dirty && (
        <button
          type="button"
          onClick={onCancel}
          disabled={saving}
          className="min-h-[44px] px-4 rounded-[10px] text-sm text-ink hover:opacity-70 transition-opacity"
        >
          {t('directory.admin.cancel')}
        </button>
      )}
      <span role="status" className="text-sm">
        {failed ? (
          <span className="text-danger-600">{t('directory.admin.saveFailed')}</span>
        ) : dirty ? (
          <span className="text-ash">{t('directory.admin.unsavedChanges')}</span>
        ) : justSaved ? (
          <span className="text-ink">{t('directory.admin.saved')}</span>
        ) : null}
      </span>
    </div>
  );
}

/**
 * Editable copy of some listing fields. While the user hasn't changed anything,
 * it follows the live listing; after a save it picks up the stored values.
 */
export function useFormDraft<T>(source: T) {
  const sourceKey = JSON.stringify(source);
  const [draft, setDraft] = useState<T>(source);
  const [baseKey, setBaseKey] = useState(sourceKey);
  const [justSaved, setJustSaved] = useState(false);
  const [failed, setFailed] = useState(false);
  const latest = useRef({ source, sourceKey });
  latest.current = { source, sourceKey };
  const dirty = JSON.stringify(draft) !== baseKey;

  useEffect(() => {
    if (!dirty) {
      setDraft(latest.current.source);
      setBaseKey(latest.current.sourceKey);
    }
    // Only when the stored listing changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sourceKey]);

  useEffect(() => {
    if (!justSaved) return;
    const timer = setTimeout(() => setJustSaved(false), 2500);
    return () => clearTimeout(timer);
  }, [justSaved]);

  const update = useCallback((patch: Partial<T>) => {
    setFailed(false);
    setDraft((d) => ({ ...d, ...patch }));
  }, []);

  const reset = useCallback(() => {
    setFailed(false);
    setDraft(latest.current.source);
    setBaseKey(latest.current.sourceKey);
  }, []);

  /** Runs the save; on success the form shows the stored values again. */
  const save = useCallback(async (write: () => Promise<unknown>) => {
    setFailed(false);
    try {
      await write();
      setDraft(latest.current.source);
      setBaseKey(latest.current.sourceKey);
      setJustSaved(true);
      return true;
    } catch {
      setFailed(true);
      return false;
    }
  }, []);

  return { draft, setDraft, update, dirty, reset, save, justSaved, failed };
}
